import { DisciplineEvaluationAndDeadlines } from '@/models/evaluation-and-deadlines/evaluation-and-deadlines.model';
import { ENDPOINTS } from '@/settings/endpoints';
import { SolutionsByQuestionId } from '@/models/undergrad/services/dex.services.model';
import axios, { AxiosInstance } from 'axios';
import VueCookie from 'vue-cookie';

export enum DiscursiveExerciseType {
  PRACTICE_EXERCISE = 'practiceExercise',
  DISCURSIVE_EXERCISE = 'discursiveExercise',
}

interface CollaborationResponse<T> {
  data: T;
  message: string;
  identifier: string;
}

export interface ObjectiveExerciseAnswer {
  dexExerciseId: number;
  dexUserId: number;
  assertionId: number;
  answer: any;
}

export interface DiscursiveExerciseAnswer {
  body: string;
  exerciseId: string;
  dexExerciseId: number;
  courseId?: string;
  disciplineInstanceId?: string;
  lessonId?: string;
  attemptCount?: number;
  attachments?: any[];
  studyWindowId?: string;
}

export interface PracticeExerciseUpdateAnswer {
  body: string;
  exerciseId: string;
  studyWindowId?: string;
  attachments?: any[];
  dexExerciseId: number;
  dexCommentId: number;
  dexTextId: number;
}

interface DexExerciseCommentAttachmentDTO {
  commentId: number;
  fileName: string;
  fileSize: number;
  fileType: string;
  fileUrl: string;
  id: number;
  userId: number;
}

interface DexContentDTO {
  id: number;
  userId: number;
  textId: number;
  commentId: number;
}

interface DexCommentDTO {
  attemptCount: number;
  contentsByCommentId?: number[];
  exerciseCommentAttachmentsByCommentId: number[];
  id: number;
  questionId: number;
  createdAt: string;
  updatedAt: string;
  userId: number;
}

export interface DexTextDTO {
  body: string;
  id: number;
  userId: number;
}

interface DisciplineEvaluationAndDeadlinesDTO {
  disciplineInstanceIds: string[];
  studyWindowId: string;
}

export interface DexCommentResponse {
  exerciseCommentAttachments?: DexExerciseCommentAttachmentDTO[];
  comment: DexCommentDTO;
  content: DexContentDTO;
  message: string;
  success: boolean;
  text: DexTextDTO;
}

export interface DexReplyResponse {
  id: number;
  questionId: number;
  userId: number;
  alternativeId: number;
  questionByQuestionId: {
    solutionsByQuestionIdList: SolutionsByQuestionId[];
  };
}

export enum RouteExerciseType {
  REVIEW_LIST = 'review-list',
  MULTIPLE_CHOICE = 'multiple-choice',
  OBJECTIVE_THINK_AND_ANSWER = 'objective-think-and-answer',
}

export class ExercisesEvaluationService {
  private gateway: AxiosInstance;

  constructor() {
    const dexToken = localStorage.getItem('token');
    const d = VueCookie.get('d', { domain: '.descomplica.com.br' });

    this.gateway = axios.create({
      baseURL: ENDPOINTS.pegasusPepeLegal,
      timeout: 10000,
      headers: {
        'Authorization-Dex': `Bearer ${dexToken}`,
        Authorization: `Bearer ${d}`,
      },
    });
  }

  private async sendObjectiveReply(
    data: ObjectiveExerciseAnswer,
    routeType: RouteExerciseType,
  ): Promise<DexReplyResponse> {
    const { assertionId, dexExerciseId, dexUserId, answer } = data;

    const { data: dexCreatedReplyResponse } = await this.gateway.post<
      CollaborationResponse<{ reply: DexReplyResponse }>
    >(`/send-reply/${routeType}`, {
      dexQuestionId: dexExerciseId,
      assertionId,
      dexUserId,
      answer,
    });

    return dexCreatedReplyResponse.data.reply;
  }

  async sendMultipleChoiceAnswer(data: ObjectiveExerciseAnswer) {
    return this.sendObjectiveReply(data, RouteExerciseType.MULTIPLE_CHOICE);
  }

  async sendReviewListAnswer(data: ObjectiveExerciseAnswer) {
    return this.sendObjectiveReply(data, RouteExerciseType.REVIEW_LIST);
  }

  async sendObjectiveThinkAndAnswer(data: ObjectiveExerciseAnswer) {
    return this.sendObjectiveReply(data, RouteExerciseType.OBJECTIVE_THINK_AND_ANSWER);
  }

  async sendDiscursiveExerciseAnswer(
    activityType: DiscursiveExerciseType,
    {
      body,
      exerciseId,
      dexExerciseId,
      courseId,
      disciplineInstanceId,
      lessonId,
      attachments,
      attemptCount,
      studyWindowId,
    }: DiscursiveExerciseAnswer,
  ): Promise<DexCommentResponse> {
    const isFromLegacy = Number.parseInt(exerciseId, 10) === dexExerciseId;
    const extraBody = !isFromLegacy
      ? {
          exerciseId,
          courseId,
          disciplineInstanceId,
          lessonId,
          studyWindowId,
        }
      : {};

    const { data: dexCreatedCommentResponse } = await this.gateway.post<
      CollaborationResponse<DexCommentResponse>
    >('/send-activity-answer-to-evaluate', {
      body,
      activityType,
      attachments,
      attemptCount,
      questionId: dexExerciseId,
      ...extraBody,
    });

    return dexCreatedCommentResponse.data;
  }

  async updatePracticeExerciseAnswer({
    body,
    dexExerciseId,
    dexCommentId,
    dexTextId,
    exerciseId,
    attachments,
    studyWindowId,
  }: PracticeExerciseUpdateAnswer): Promise<DexCommentResponse> {
    const isFromLegacy = Number.parseInt(exerciseId, 10) === dexExerciseId;

    const extraBody = !isFromLegacy ? { exerciseId, studyWindowId } : {};

    const { data: dexUpdatedCommentResponse } = await this.gateway.post<
      CollaborationResponse<DexCommentResponse>
    >('/update-activity-answer-to-evaluate', {
      body,
      attachments,
      questionId: dexExerciseId,
      commentId: dexCommentId,
      textId: dexTextId,
      ...extraBody,
    });

    return dexUpdatedCommentResponse.data;
  }

  async getEvaluationAndDeadlines({
    disciplineInstanceIds,
    studyWindowId,
  }: DisciplineEvaluationAndDeadlinesDTO): Promise<DisciplineEvaluationAndDeadlines> {
    const {
      data: { data: evaluationAndDeadlines },
    } = await this.gateway.get<CollaborationResponse<DisciplineEvaluationAndDeadlines>>(
      '/evaluation-model-and-deadlines',
      {
        params: {
          studyWindowId,
          disciplineInstanceIds,
        },
      },
    );

    return evaluationAndDeadlines;
  }
}
